/**
 * PlacesAutocomplete - Google Places Autocomplete input component
 * Uses the new AutocompleteSuggestion API with custom dropdown
 * Supports 'studio' mode (establishment search), 'address' mode, and 'city' mode
 */

import { useEffect, useRef, useState, useCallback } from 'react';
import { useGoogleMaps } from '../../hooks/useGoogleMaps';

export interface PlaceResult {
  lat: number;
  lng: number;
  formattedAddress: string;
  name?: string;
  /** Google Places place_id of the selected prediction (city mode: the locality). */
  placeId?: string;
  addressComponents?: {
    street?: string;
    city?: string;
    state?: string;
    zip?: string;
    /** ISO 3166-1 alpha-2 country code, e.g. "US" or "CA". */
    country?: string;
  };
}

interface Suggestion {
  placeId: string;
  mainText: string;
  secondaryText: string;
  placePrediction: google.maps.places.PlacePrediction;
}

interface PlacesAutocompleteProps {
  mode: 'studio' | 'address' | 'city';
  onPlaceSelected: (place: PlaceResult) => void;
  placeholder?: string;
  className?: string;
  defaultValue?: string;
  disabled?: boolean;
  /** Optional input id, so a <label htmlFor> can associate with the field. */
  id?: string;
  /**
   * Controlled value. When provided, the component renders this text and the
   * parent owns it — every keystroke fires onInputChange and the suggestion
   * fetch keys off this value. Lets a form use the field itself as the search
   * box (e.g. a City field). Omit it to keep the legacy uncontrolled behavior.
   */
  value?: string;
  /** Fires on every manual keystroke (controlled usage). Not called on select. */
  onInputChange?: (text: string) => void;
}

const defaultPlaceholders: Record<string, string> = {
  studio: 'Search by studio name...',
  address: 'Enter an address...',
  city: 'Search for a city...',
};

/** Map mode to includedPrimaryTypes for the new Places API */
const modeTypeMap: Record<string, string[]> = {
  studio: ['establishment'],
  address: [],
  // locality only: an administrative_area_level_3 (civil township) pick returns
  // a non-locality place_id and often no `locality` address component, which
  // would store a wrong/unstable canonical key. Restricting to locality keeps
  // google_place_id a true city key.
  city: ['locality'],
};

export function PlacesAutocomplete({
  mode,
  onPlaceSelected,
  placeholder,
  className = '',
  defaultValue = '',
  disabled = false,
  id,
  value,
  onInputChange,
}: PlacesAutocompleteProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const { isLoaded, error: mapsError } = useGoogleMaps();
  const [inputValue, setInputValue] = useState(defaultValue);
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [isSelecting, setIsSelecting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const confirmedValueRef = useRef<string>('');
  // The last text the USER actually typed. The debounced fetch only fires for
  // this value, so programmatic value changes — mount pre-fill, a parent form
  // autofilling the city from a different field, or our own post-select set —
  // never open a suggestions dropdown.
  const lastTypedRef = useRef<string | null>(null);

  // Controlled when `value` is provided; otherwise fall back to internal state.
  const isControlled = value !== undefined;
  const text = isControlled ? value : inputValue;

  const handleTextChange = useCallback((next: string) => {
    lastTypedRef.current = next;
    if (isControlled) onInputChange?.(next);
    else setInputValue(next);
  }, [isControlled, onInputChange]);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const sessionTokenRef = useRef<google.maps.places.AutocompleteSessionToken | null>(null);

  const getSessionToken = useCallback(() => {
    if (!sessionTokenRef.current && isLoaded) {
      sessionTokenRef.current = new google.maps.places.AutocompleteSessionToken();
    }
    return sessionTokenRef.current;
  }, [isLoaded]);

  const resetSessionToken = useCallback(() => {
    sessionTokenRef.current = null;
  }, []);

  const fetchSuggestions = useCallback(async (input: string) => {
    if (!isLoaded || input.length < 2) {
      setSuggestions([]);
      setShowDropdown(false);
      setIsLoading(false);
      return;
    }

    // Open the dropdown immediately and show a loading row so the user knows
    // suggestions are coming / selectable.
    setIsLoading(true);
    setShowDropdown(true);

    try {
      const types = modeTypeMap[mode] || [];
      const request: google.maps.places.AutocompleteRequest = {
        input,
        // US + Canada — covers Canadian tour stops/venues/cities (SDA-184).
        includedRegionCodes: ['us', 'ca'],
        // Force English results regardless of browser locale (belt-and-suspenders
        // alongside the loader-level language setting).
        language: 'en',
        sessionToken: getSessionToken() ?? undefined,
      };

      if (types.length > 0) {
        request.includedPrimaryTypes = types;
      }

      const { suggestions: results } =
        await google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions(request);

      const mapped: Suggestion[] = [];
      for (const s of results) {
        if (!s.placePrediction) continue;
        mapped.push({
          placeId: s.placePrediction.placeId,
          mainText: s.placePrediction.mainText?.text || '',
          secondaryText: s.placePrediction.secondaryText?.text || '',
          placePrediction: s.placePrediction,
        });
      }

      setSuggestions(mapped);
      setShowDropdown(mapped.length > 0);
      setActiveIndex(-1);
    } catch {
      setSuggestions([]);
      setShowDropdown(false);
    } finally {
      setIsLoading(false);
    }
  }, [isLoaded, mode, getSessionToken]);

  const handleSelect = useCallback(async (suggestion: Suggestion) => {
    setIsSelecting(true);
    setShowDropdown(false);
    setSuggestions([]);

    try {
      const placeHandle = suggestion.placePrediction.toPlace();
      const { place } = await placeHandle.fetchFields({
        fields: ['displayName', 'formattedAddress', 'location', 'addressComponents'],
      });

      if (!place.location) {
        console.warn('[PlacesAutocomplete] No location returned for place:', suggestion.placeId);
        return;
      }

      const components = place.addressComponents || [];
      const getComponent = (type: string, useShort = false): string | undefined => {
        const comp = components.find((c) => c.types.includes(type));
        return comp ? (useShort ? comp.shortText : comp.longText) ?? undefined : undefined;
      };

      const result: PlaceResult = {
        lat: place.location.lat(),
        lng: place.location.lng(),
        formattedAddress: place.formattedAddress || '',
        name: place.displayName || undefined,
        placeId: suggestion.placeId || undefined,
        addressComponents: {
          street: [
            getComponent('street_number'),
            getComponent('route'),
          ].filter(Boolean).join(' ') || undefined,
          city: getComponent('locality') || getComponent('sublocality'),
          state: getComponent('administrative_area_level_1', true),
          zip: getComponent('postal_code'),
          // ISO 3166-1 alpha-2 code — short form, e.g. "US" or "CA".
          country: getComponent('country', true),
        },
      };

      // City mode shows just the locality name ("New York"), not the full
      // formatted address ("New York, NY, USA"). studio/address keep their text.
      const cityName = result.addressComponents?.city || place.displayName || place.formattedAddress || '';
      const displayText = mode === 'studio'
        ? (place.displayName || place.formattedAddress || '')
        : mode === 'city'
          ? cityName
          : (place.formattedAddress || '');
      confirmedValueRef.current = displayText;
      // In controlled mode the parent's onPlaceSelected sets the value (city +
      // state + place_id), so we must NOT also call onInputChange here — that
      // would clear the just-captured place_id. Only sync internal state when
      // uncontrolled.
      if (!isControlled) setInputValue(displayText);

      resetSessionToken();
      onPlaceSelected(result);
    } catch (err) {
      console.error('[PlacesAutocomplete] Failed to fetch place details:', err);
    } finally {
      setIsSelecting(false);
    }
  }, [mode, onPlaceSelected, resetSessionToken, isControlled]);

  // Debounced fetch on input change (keys off the effective text — controlled
  // `value` or internal `inputValue`).
  useEffect(() => {
    if (isSelecting) return;

    // Only search for text the user actually typed — ignore programmatic value
    // changes (mount pre-fill, cross-field autofill, post-select) so they don't
    // pop a dropdown / overlap another autocomplete on the same form.
    if (text !== lastTypedRef.current) return;

    // Don't re-fetch if input matches the confirmed selection
    if (text === confirmedValueRef.current) return;
    // Clear confirmed value once user starts editing
    confirmedValueRef.current = '';

    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (text.length < 2) {
      setSuggestions([]);
      setShowDropdown(false);
      setIsLoading(false);
      return;
    }

    debounceRef.current = setTimeout(() => {
      void fetchSuggestions(text);
    }, 300);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [text, fetchSuggestions, isSelecting]);

  // Keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!showDropdown || suggestions.length === 0) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((prev) => Math.min(prev + 1, suggestions.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((prev) => Math.max(prev - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const selected = suggestions[activeIndex];
      if (activeIndex >= 0 && selected) {
        void handleSelect(selected);
      }
    } else if (e.key === 'Escape') {
      setShowDropdown(false);
    }
  };

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const container = inputRef.current?.parentElement;
      if (container && !container.contains(e.target as Node)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Update input value when defaultValue changes externally (uncontrolled only;
  // when controlled, the parent owns `value`).
  useEffect(() => {
    if (!isControlled) setInputValue(defaultValue);
  }, [defaultValue, isControlled]);

  if (mapsError) {
    return (
      <input
        id={id}
        type="text"
        value={text}
        onChange={(e) => handleTextChange(e.target.value)}
        placeholder={placeholder || 'Enter location...'}
        className={className}
        disabled={disabled}
      />
    );
  }

  return (
    <div className="relative">
      <input
        ref={inputRef}
        id={id}
        type="text"
        value={text}
        onChange={(e) => handleTextChange(e.target.value)}
        onKeyDown={handleKeyDown}
        onFocus={() => (suggestions.length > 0 || isLoading) && setShowDropdown(true)}
        placeholder={placeholder || defaultPlaceholders[mode] || 'Search...'}
        className={className}
        // Stay typeable even before Maps loads (or if it's blocked) — these fields
        // double as manual entry. Suggestions are gated on isLoaded in the fetch.
        disabled={disabled}
        autoComplete="off"
        role="combobox"
        aria-expanded={showDropdown}
        aria-haspopup="listbox"
        aria-busy={isLoading}
        aria-activedescendant={activeIndex >= 0 ? `place-option-${activeIndex}` : undefined}
      />

      {showDropdown && (isLoading || suggestions.length > 0) && (
        <ul
          role="listbox"
          className="absolute z-50 w-full mt-1 bg-white border border-warm-200 rounded-lg shadow-lg max-h-60 overflow-auto"
        >
          {isLoading && (
            <li className="px-4 py-3 flex items-center gap-2 text-sm text-warm-500" aria-disabled="true">
              <span className="inline-block w-3.5 h-3.5 border-2 border-warm-300 border-t-primary-600 rounded-full animate-spin" />
              Searching…
            </li>
          )}
          {!isLoading && suggestions.map((suggestion, index) => (
            <li
              key={suggestion.placeId}
              id={`place-option-${index}`}
              role="option"
              aria-selected={index === activeIndex}
              className={`px-4 py-3 cursor-pointer transition-colors ${
                index === activeIndex
                  ? 'bg-primary-50 text-primary-900'
                  : 'hover:bg-warm-50 text-warm-800'
              }`}
              onMouseDown={(e) => {
                e.preventDefault();
                void handleSelect(suggestion);
              }}
              onMouseEnter={() => setActiveIndex(index)}
            >
              <div className="font-medium text-sm">{suggestion.mainText}</div>
              {suggestion.secondaryText && (
                <div className="text-xs text-warm-500 mt-0.5">{suggestion.secondaryText}</div>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default PlacesAutocomplete;
